/**
 * 頁 12 — 場後結算（`ui-spec` 頁 12；P1-10）
 *
 * 直銷場交收（`delivered`）之後：逐項填「賣出／剩貨／損壞」（箱 ＋ 散件）＋ 剩貨去向，
 * 對得上才可以**確認入帳**（`delivered → reconciled`：一次過出 `SALE`／`TRANSFER`／`ADJUST`），
 * 之後職員／管理員按「完單」（`reconciled → closed`）。
 *
 * 紅線（同後端一致；前端先擋係為咗好體驗，**後端才係權威**）：只有直銷場要結算、
 * 對數／去向／散件一律交模組 `@coeldery/event-reconciliation` 判、確認後唔准改、帳目一律後端出。
 * 表單住 `components/ReconcileForm.tsx`（硬規則 B：頁面檔 ≤200 行）。
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import ReconcileForm from '../components/ReconcileForm';
import ReconcileMovements from '../components/ReconcileMovements';
import { OutboundStatusChip, kindLabelKey, optionLabel } from '../components/OutboundStatusChip';
import { btnGhost, btnPrimary, card, errText, hint as hintStyle } from '@coeldery/ui-kit';
import {
  ApiError,
  api,
  type Me,
  type ReconcileDetail,
  type ReconcileMovement,
  type ReconcileResult,
} from '../lib/api';

export default function Reconcile({ user }: { user: Me }) {
  const { t, i18n } = useTranslation();
  const { id = '' } = useParams();
  const [data, setData] = useState<ReconcileDetail | null>(null);
  /** 今次確認／完單出嘅移動（重新載入後詳情唔帶帳 —— 睇帳本連結） */
  const [movements, setMovements] = useState<ReconcileMovement[]>([]);
  const [justDone, setJustDone] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api.reconciliation(id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const order = data?.order ?? null;
  /** 完單只有職員／管理員（後端一樣擋） */
  const canClose = user.role === 'staff' || user.role === 'admin';

  /** 去向標籤（中／英由 DB 出，前端按語言揀 —— 唔准寫死清單） */
  const dispositionLabel = (code: string): string => {
    const d = data?.dispositions.find((x) => x.code === code);
    return d ? optionLabel(d, i18n.language) : code;
  };
  const itemName = (lineId: string): string =>
    data?.base.find((b) => b.lineId === lineId)?.itemName ?? '—';
  const qtyText = (cartons: number, units: number): string =>
    `${cartons} ${t('recon.cartons')}${units > 0 ? ` + ${units} ${t('recon.units')}` : ''}`;

  const onSaved = async (res: ReconcileResult) => {
    setMovements(res.movements);
    setJustDone(res.confirmed ? t('recon.confirmed') : t('recon.savedDraft'));
    await load();
  };

  const close = async () => {
    if (!window.confirm(t('recon.closeAsk', { orderNo: order?.orderNo ?? '' }))) return;
    setBusy(true);
    setError(null);
    try {
      await api.closeOutbound(id);
      setJustDone(t('recon.closedOk'));
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800 }}>{t('recon.title')}</h2>

        <Link to={`/outbound/${id}`} style={btnGhost}>
          {t('recon.backToOrder')}
        </Link>
      </div>

      {loading && <p style={hintStyle}>{t('common.loading')}</p>}
      {error && (
        <p style={errText} role="alert">
          {error}
        </p>
      )}

      {order && data && (
        <>
          <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <strong style={{ fontSize: '16px' }}>{order.orderNo}</strong>
              <OutboundStatusChip status={order.status} />
              <span style={hintStyle}>{t(kindLabelKey(order.outboundKind))}</span>
            </div>
            <p style={hintStyle}>{t('recon.intro')}</p>
            {justDone && (
              <p style={{ ...hintStyle, fontWeight: 700, color: 'var(--color-text)' }}>
                {justDone}
              </p>
            )}
          </section>

          {data.canEdit ? (
            <ReconcileForm data={data} onSaved={onSaved} />
          ) : (
            <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
              {data.lines.length > 0 && (
                <>
                  <h3 style={{ margin: '0 0 6px', fontSize: '16px', fontWeight: 800 }}>
                    {t('recon.savedLines')}
                  </h3>
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {data.lines.map((l) => (
                      <li
                        key={l.id}
                        style={{ padding: '8px 0', borderTop: '1px solid var(--color-divider)' }}
                      >
                        <strong>{itemName(l.outboundLineId)}</strong>
                        <p style={hintStyle}>
                          {t('recon.sold')}：{qtyText(l.soldCartons, l.soldUnits)}
                          {' · '}
                          {t('recon.leftover')}：{qtyText(l.leftoverCartons, l.leftoverUnits)}
                          {' · '}
                          {t('recon.damaged')}：{qtyText(l.damagedCartons, l.damagedUnits)}
                        </p>
                        {l.dispositionCode && (
                          <p style={hintStyle}>
                            {t('recon.disposition')}：{dispositionLabel(l.dispositionCode)}
                            {' · '}
                            {t('recon.toLoc')}：{l.toLoc ?? '—'}
                          </p>
                        )}
                        {l.note && (
                          <p style={hintStyle}>
                            {t('recon.note')}：{l.note}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {data.reconciliation?.confirmedAt && (
                <p style={hintStyle}>
                  {t('recon.confirmedAt')}：{data.reconciliation.confirmedAt.slice(0, 16).replace('T', ' ')}（
                  {data.reconciliation.confirmedBy ?? '—'}）
                </p>
              )}
              {data.errors.map((e) => (
                <p key={e} style={errText} role="alert">
                  {e}
                </p>
              ))}

              {order.status === 'reconciled' && canClose && (
                <button
                  type="button"
                  style={btnPrimary}
                  onClick={() => void close()}
                  disabled={busy}
                >
                  {busy ? t('common.saving') : t('recon.closeOrder')}
                </button>
              )}
              {order.status === 'reconciled' && !canClose && (
                <p style={hintStyle}>{t('recon.needStaffToClose')}</p>
              )}
              {order.status === 'closed' && <p style={hintStyle}>{t('recon.closedHint')}</p>}
              {order.status !== 'reconciled' && order.status !== 'closed' && (
                <p style={hintStyle}>{t('recon.onlyDirectSale')}</p>
              )}
            </section>
          )}

          <ReconcileMovements movements={movements} />
        </>
      )}
    </>
  );
}

